import {
  type DragSourceData,
  type DropTargetData,
  type HorizontalEdge,
  isNewPanelDragData,
  isPanelDragData,
  type PanelTargetData,
  type RowTargetData
} from './panel-dnd';
import {
  AUTO_PANEL_SIZE,
  type PageCmsPanel,
  type PageCmsPanelRow,
  type PageCmsPanels,
  type PanelConfigTypename,
  type PanelSize
} from './panel-types';

const MAX_PANELS_PER_ROW = 3;

interface PanelPosition {
  rowIndex: number;
  panelIndex: number;
}

export interface PanelDropPreview {
  rowIndex: number;
  row: PageCmsPanelRow;
  incomingPanelId: string;
}

export const canDropOnPanel = (
  sourceData: Record<string, unknown>,
  targetPanelId: string,
  targetRowIndex: number,
  targetPanelCount: number
): boolean => {
  if (isPanelDragData(sourceData)) {
    if (sourceData.panelId === targetPanelId) {
      return sourceData.rowIndex === targetRowIndex;
    }

    return targetPanelCount < MAX_PANELS_PER_ROW || sourceData.rowIndex === targetRowIndex;
  }

  return isNewPanelDragData(sourceData) && targetPanelCount < MAX_PANELS_PER_ROW;
};

/** Row whitespace accepts new panels and panels from other rows; same-row moves must enter another panel. */
export const canDropOnRow = (sourceData: Record<string, unknown>, rowIndex: number, panelCount: number): boolean => {
  if (isPanelDragData(sourceData)) {
    return sourceData.rowIndex !== rowIndex && panelCount < MAX_PANELS_PER_ROW;
  }

  return isNewPanelDragData(sourceData) && panelCount < MAX_PANELS_PER_ROW;
};

/** The two zones hugging a single-panel row are no-ops for that row's own panel. */
export const canDropOnRowDropZone = (sourceData: Record<string, unknown>, rowIndex: number): boolean => {
  if (isPanelDragData(sourceData)) {
    return !(sourceData.isOnlyPanelInRow && (rowIndex === sourceData.rowIndex || rowIndex === sourceData.rowIndex + 1));
  }

  return isNewPanelDragData(sourceData);
};

const resetPanelSize = (panel: PageCmsPanel): PageCmsPanel =>
  panel.settings.size === AUTO_PANEL_SIZE
    ? panel
    : { ...panel, settings: { ...panel.settings, size: AUTO_PANEL_SIZE } as PageCmsPanel['settings'] };

const normalizePanelRowSize = (row: PageCmsPanelRow): PageCmsPanelRow => {
  if (row.length === 2) {
    return row;
  }

  const normalized = row.map(resetPanelSize);
  return normalized.every((panel, index) => panel === row[index]) ? row : normalized;
};

/** Wide sizing only applies to 2-panel rows; every other row shape is stored as auto. */
export const normalizePanelSizes = (panels: PageCmsPanels): PageCmsPanels => {
  const normalized = panels.map(normalizePanelRowSize);
  return normalized.every((row, index) => row === panels[index]) ? panels : normalized;
};

/*
 * A partial update for one member of the config union. Plain Partial<union> collapses to the
 * common keys (just `__typename`), so the conditional type distributes Partial over each member.
 */
export type PanelConfigPatch<C = PageCmsPanel['settings']> = C extends { __typename: string }
  ? Partial<Omit<C, '__typename' | 'type' | 'size'>>
  : never;

/**
 * Sets the size of one panel in a row. Mirrors the legacy editor's `resizeRowPanels`:
 * every panel in the row is reset to "auto" first, then the chosen panel gets the new size,
 * so a 2-panel row can never contain two "wide" panels.
 *
 * Returns a new array; Apollo query results are frozen, so panels must never be mutated in place.
 */
export const updatePanelSize = (
  panels: PageCmsPanels,
  rowIndex: number,
  panelId: string,
  size: PanelSize
): PageCmsPanels =>
  panels.map((row, index) =>
    index === rowIndex
      ? row.map((panel) => ({
          ...panel,
          settings: {
            ...panel.settings,
            size: panel.id === panelId ? size : AUTO_PANEL_SIZE
          }
        }))
      : row
  );

export const deletePanel = (panels: PageCmsPanels, rowIndex: number, panelId: string): PageCmsPanels =>
  normalizePanelSizes(
    panels
      .map((row, index) => (index === rowIndex ? row.filter((panel) => panel.id !== panelId) : row))
      .filter((row) => row.length > 0)
  );

/**
 * Moves a panel to `target`, whose `panelIndex` is an insertion index in the target row as currently
 * rendered. Returns the input unchanged for no-op moves. Cross-row moves remove and insert in a single
 * pass over the original row indexes, then drop the source row if it emptied.
 */
export const movePanel = (panels: PageCmsPanels, source: PanelPosition, target: PanelPosition): PageCmsPanels => {
  if (source.rowIndex === target.rowIndex) {
    // Removing the source shifts later panels left by one, so an insertion index past it drops by one.
    const insertIndex = target.panelIndex > source.panelIndex ? target.panelIndex - 1 : target.panelIndex;

    if (insertIndex === source.panelIndex) {
      return panels;
    }

    const row = panels[source.rowIndex];
    const withoutSource = row.filter((_, index) => index !== source.panelIndex);
    const reordered = [
      ...withoutSource.slice(0, insertIndex),
      row[source.panelIndex],
      ...withoutSource.slice(insertIndex)
    ];

    return panels.map((current, index) => (index === source.rowIndex ? reordered : current));
  }

  const panel = resetPanelSize(panels[source.rowIndex][source.panelIndex]);

  return normalizePanelSizes(
    panels
      .map((row, index) => {
        if (index === source.rowIndex) {
          return row.filter((_, position) => position !== source.panelIndex);
        }

        if (index === target.rowIndex) {
          return [...row.slice(0, target.panelIndex), panel, ...row.slice(target.panelIndex)];
        }

        return row;
      })
      .filter((row) => row.length > 0)
  );
};

/**
 * Moves a panel into a new single-panel row at `targetRowIndex`, a drop zone's insertion index in the
 * current grid (0 = above the first row, panels.length = below the last). Returns the input unchanged
 * when a row's only panel targets one of its own adjacent zones.
 */
export const movePanelToNewRow = (
  panels: PageCmsPanels,
  source: PanelPosition,
  targetRowIndex: number
): PageCmsPanels => {
  const sourceRow = panels[source.rowIndex];

  if (sourceRow.length === 1 && (targetRowIndex === source.rowIndex || targetRowIndex === source.rowIndex + 1)) {
    return panels;
  }

  const withSourceRemoved = panels.map((row, index) =>
    index === source.rowIndex ? row.filter((_, position) => position !== source.panelIndex) : row
  );

  // Splice at the pre-filter index, then drop the emptied source row (never the new single-panel row).
  return normalizePanelSizes(
    [
      ...withSourceRemoved.slice(0, targetRowIndex),
      [resetPanelSize(sourceRow[source.panelIndex])],
      ...withSourceRemoved.slice(targetRowIndex)
    ].filter((row) => row.length > 0)
  );
};

export const insertPanel = (panels: PageCmsPanels, target: PanelPosition, panel: PageCmsPanel): PageCmsPanels =>
  normalizePanelSizes(
    panels.map((row, index) =>
      index === target.rowIndex ? [...row.slice(0, target.panelIndex), panel, ...row.slice(target.panelIndex)] : row
    )
  );

export const insertPanelAsNewRow = (panels: PageCmsPanels, rowIndex: number, panel: PageCmsPanel): PageCmsPanels => [
  ...panels.slice(0, rowIndex),
  [panel],
  ...panels.slice(rowIndex)
];

const getInsertPosition = (
  panels: PageCmsPanels,
  target: PanelTargetData | RowTargetData,
  edge: HorizontalEdge | null
): PanelPosition =>
  target.type === 'panel'
    ? { rowIndex: target.rowIndex, panelIndex: target.panelIndex + (edge === 'right' ? 1 : 0) }
    : { rowIndex: target.rowIndex, panelIndex: panels[target.rowIndex].length };

export const resolveDrop = (
  panels: PageCmsPanels,
  source: DragSourceData,
  target: DropTargetData,
  edge: HorizontalEdge | null,
  createNewPanel: (typename: PanelConfigTypename) => PageCmsPanel
): PageCmsPanels => {
  if (source.type === 'new-panel') {
    const panel = createNewPanel(source.typename);

    return target.type === 'row-drop-zone'
      ? insertPanelAsNewRow(panels, target.rowIndex, panel)
      : insertPanel(panels, getInsertPosition(panels, target, edge), panel);
  }

  const from = { rowIndex: source.rowIndex, panelIndex: source.panelIndex };

  if (target.type === 'row-drop-zone') {
    return movePanelToNewRow(panels, from, target.rowIndex);
  }

  if (target.rowIndex !== source.rowIndex) {
    return movePanel(panels, from, getInsertPosition(panels, target, edge));
  }

  return target.type === 'row'
    ? panels
    : movePanel(panels, from, {
        rowIndex: target.rowIndex,
        panelIndex: target.panelIndex + (source.panelIndex < target.panelIndex ? 1 : 0)
      });
};

export const getPanelDropPreview = (
  panels: PageCmsPanels,
  source: DragSourceData,
  target: DropTargetData,
  edge: HorizontalEdge | null,
  newPanel: PageCmsPanel | null
): PanelDropPreview | null => {
  if (target.type === 'row-drop-zone') {
    return null;
  }

  const incomingPanel = source.type === 'panel' ? panels[source.rowIndex]?.[source.panelIndex] : newPanel;

  if (!incomingPanel) {
    return null;
  }

  const projectedPanels = resolveDrop(panels, source, target, edge, () => incomingPanel);

  if (projectedPanels === panels) {
    return null;
  }

  const projectedRow = projectedPanels.find((row) => row.some((panel) => panel.id === incomingPanel.id));

  // Overlay the row under the pointer: its projected index shifts if an earlier single-panel source row empties.
  return projectedRow ? { rowIndex: target.rowIndex, row: projectedRow, incomingPanelId: incomingPanel.id } : null;
};

/**
 * Merges a partial config patch into one panel's config. Returns new arrays/objects —
 * Apollo query results are frozen, so panels must never be mutated in place.
 */
export const updatePanelConfig = (
  panels: PageCmsPanels,
  rowIndex: number,
  panelId: string,
  patch: PanelConfigPatch
): PageCmsPanels =>
  panels.map((row, index) =>
    index === rowIndex
      ? row.map((panel) =>
          panel.id === panelId
            ? // Editors only send patches for their own settings' __typename; TS can't correlate
              // the patch member with the runtime settings member, hence the cast.
              { ...panel, settings: { ...panel.settings, ...patch } as PageCmsPanel['settings'] }
            : panel
        )
      : row
  );
